import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import { RuleObject } from 'antd/es/form';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import LeiLoginlessFooter from 'components/molecules/Lei/zadostLeiLoginless/LeiLoginlessFooter';
import LeiLoginlessMenu from 'components/molecules/Lei/zadostLeiLoginless/LeiLoginlessMenu';
import LoginCollapse from 'components/molecules/LoginCollapse';
import { callCaptcha } from 'components/pages/Registrace';

import { useLeiRedirect } from 'hooks/lei/useLeiRedirect';
import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { ICaptcha } from 'services/captcha';
import {
    EEmailStav,
    EStavUzivatele,
    EStavZadostiLoginlessLei,
    ILeiLoginlessCheckUserResp,
    IRozpZadostData,
    leiEmailOvereni,
    leiLoginlessUserCheck,
    leiLoginlessZalozitZadost
} from 'services/lei/leiLoginLess';
import { ETypPrihlaseni } from 'services/profily';

import { EAnoNe } from 'enums/anoNe';
import { emailRegExp } from 'enums/regExp';
import routePathEnum from 'enums/routePathEnum';

import anoNe from 'data/codebooks/anoNeSelect';
import { leiTypOsobySelect } from 'data/codebooks/lei/leiTypOsobySelect';

import dicValidation from 'utils/validations/dicValidation';
import emailValidation from 'utils/validations/emailValidation';
import phoneValidation from 'utils/validations/phoneValidation';

interface IIdentifikaceOsoby {}

const IdentifikaceOsoby: React.FC<IIdentifikaceOsoby> = () => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { lei, language, token } = useContext(Context);
    const { setLevel } = useProfily();
    const [form] = Form.useForm();
    const [emailOvereniResp, setEmailOvereniResp] = useState<{
        stav: EEmailStav;
        hash: string;
    }>();
    const { hash } = useParams<{ hash: string }>();
    const { leiRedirect } = useLeiRedirect();
    const [userCheckResp, setUserCheckResp] = useState<ILeiLoginlessCheckUserResp | undefined>(
        lei?.loginless?.osoba?.stavzadosti === EStavZadostiLoginlessLei.ZALOZENA && lei?.loginless?.osoba?.typprihlaseni
            ? {
                  stav: EStavUzivatele.MUSI,
                  typyuctu: [lei?.loginless?.osoba?.typprihlaseni as ETypPrihlaseni]
              }
            : undefined
    );
    // const [disableDownload, setDisableDownload] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    //   const [fileErr, setFileErr] = useState<boolean>(false);
    // const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>();
    //  const [fileList, setFileList] = useState<IFileList[] | undefined>();
    // const [uploading, setUploading] = useState<boolean>(false);
    const setForceRerender = useState<boolean>(false)[1];
    const [errCaptcha, setErrCaptcha] = useState<string>();
    const [loadingCaptcha, setLoadingCaptcha] = useState<boolean>(false);
    const [captchaState, setCaptchaState] = useState<ICaptcha>({
        id: 0,
        image: ''
    });
    const [redirect, setRedirect] = useState<boolean>(false);
    const [showEmail2, setShowEmail2] = useState<boolean>(!!lei?.loginless?.osoba?.email2);

    const userCheck = (hashInput: string, kod: string, osobaData: IRozpZadostData) => {
        setLoading(true);
        leiLoginlessUserCheck({
            hash: hashInput,
            kod: kod
        })
            .then(resp => {
                setUserCheckResp(resp);
                form.setFieldsValue({
                    ...resp,
                    madic:
                        lei?.loginless?.osoba && typeof lei?.loginless.osoba?.madic === 'boolean'
                            ? lei?.loginless.osoba?.madic
                                ? EAnoNe.ANO
                                : EAnoNe.NE
                            : undefined
                });
                if (resp.stav === EStavUzivatele.EXISTUJE || resp.stav === EStavUzivatele.NEEXISTUJE) {
                    leiLoginlessZalozitZadost({
                        hash: hashInput,
                        kod: kod,
                        ...(token && {
                            token: token
                        })
                    })
                        .then(response => {
                            !token && setLevel('token', response.token);
                            setLevel('lei', {
                                ...lei,
                                idVarsymZadosti: {
                                    id: response.zadostoid,
                                    varsym: response.varsymbol
                                },
                                loginless: {
                                    ...lei?.loginless!,
                                    osoba: {
                                        ...osobaData,
                                        hash: hashInput,
                                        kod: kod
                                    }
                                }
                            });
                            setTimeout(() => setRedirect(true), 0);
                        })
                        .catch(() => setLoading(false));
                } else {
                    setLoading(false);
                    setLevel('lei', {
                        ...lei,
                        loginless: {
                            ...lei?.loginless!,
                            osoba: {
                                ...osobaData,
                                hash: hashInput,
                                kod: kod
                            }
                        }
                    });
                    setRedirect(true);
                }
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        if (!lei?.loginless && hash) {
            navigateWorkaround(routePathEnum.LEI_LOGINLESS_ODKAZ.replace(/:hash\?$/, hash));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hash]);

    useEffect(() => {
        if (redirect && token) {
            if (lei?.idVarsymZadosti?.varsym) {
                setRedirect(false);
                setTimeout(() => leiRedirect(lei?.idVarsymZadosti?.id!, lei?.idVarsymZadosti?.varsym!), 100);
            } else {
                setRedirect(false);
                leiLoginlessZalozitZadost({
                    hash: lei?.loginless?.osoba?.hash!,
                    kod: lei?.loginless?.osoba?.kod!,
                    ...(token && {
                        token: token
                    })
                })
                    .then(response => {
                        !token && setLevel('token', response.token);
                        setLevel('lei', {
                            ...lei,
                            idVarsymZadosti: {
                                id: response.zadostoid,
                                varsym: response.varsymbol
                            }
                        });
                        setTimeout(() => leiRedirect(response.zadostoid, response.varsymbol), 100);
                    })
                    .catch(() => setLoading(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirect, lei, token]);

    useEffect(() => {
        if (lei?.loginless?.osoba?.kod && !userCheckResp) {
            userCheck(lei?.loginless?.osoba?.hash, lei?.loginless?.osoba?.kod, lei?.loginless?.osoba);
        } else if (lei?.loginless?.osoba?.hash) {
            setEmailOvereniResp({
                hash: lei?.loginless?.osoba?.hash,
                stav: EEmailStav.OK
            });
        } else {
            callCaptcha(setLoadingCaptcha, setCaptchaState, setErrCaptcha);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lei?.loginless]);

    const onFinish = (fieldsValue: any) => {
        const dataToSend = {
            ...(lei?.loginless?.spolecnost?.jurisdikce && {
                jurisdikce: lei?.loginless?.spolecnost?.jurisdikce
            }),
            ...(lei?.loginless?.spolecnost?.legalentityidentifier && {
                lei: lei?.loginless?.spolecnost?.legalentityidentifier
            }),
            ...(lei?.loginless?.spolecnost?.regauthority && {
                regauthority: lei?.loginless?.spolecnost?.regauthority
            }),
            ...(lei?.loginless?.spolecnost?.registrationauthorityentityid && {
                regentity: lei?.loginless?.spolecnost?.registrationauthorityentityid
            }),
            madic: fieldsValue.madic === EAnoNe.ANO,
            ...(fieldsValue.dic && {
                dic: fieldsValue.dic
            }),
            idcaptcha: captchaState.id,
            captcha: fieldsValue.captcha,
            jmeno: fieldsValue.jmeno,
            prijmeni: fieldsValue.prijmeni,
            email1: fieldsValue.email1,
            email2: fieldsValue.email2,
            emailfakt: fieldsValue.emailfakt,
            telefon: fieldsValue.telefon,
            pozice: fieldsValue.pozice,
            typosoby: fieldsValue.typosoby,
            typzadosti: lei?.loginless?.typzadosti!
            /*     ...(prilozeneSoubory?.length
                ? {
                      soubory: prilozeneSoubory
                  }
                : {}) */
        };
        if (!emailOvereniResp) {
            setLoading(true);
            leiEmailOvereni(dataToSend)
                .then(resp => setEmailOvereniResp(resp))
                .catch((err: any) => {
                    err.response?.data.message === 'Nevalidni captcha.'
                        ? setErrCaptcha('CHYBA_REGISTRACE_401')
                        : setErrCaptcha('CHYBA_REGISTRACE_404');

                    callCaptcha(setLoadingCaptcha, setCaptchaState, setErrCaptcha);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            userCheck(emailOvereniResp.hash, fieldsValue.emailovereni, dataToSend as IRozpZadostData);
        }
    };

    console.log(userCheckResp, emailOvereniResp);

    /* const uploadProps = {
        showDownloadIcon: true,
        extramessage: disableDownload ? 'LEI_UPLOAD_FILLIN' : '',
        onButtonClick: () => {
            form.validateFields(Object.keys(form.getFieldsValue()).filter((o: any) => !String(o).includes('soubor')))
                .then(() => setDisableDownload(false))
                .catch(() => setDisableDownload(true));
        },
        beforeUpload: (file: UploadFile) => {
            // kontrola maximalnej velkosti s suboru
            if ((file.size || 0) / 1024 / 1024 > 20) {
                NotificationComponent({
                    title: 'UPLOAD_MAX_SIZE',
                    description: 'UPLOAD_MAX_SIZE_TEXT',
                    type: ENotificationType.ERROR
                });
                return false;
            }
            if (disableDownload) return false;
            return true;
        },
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    form.validateFields(['soubor']);
                    setFileErr(false);
                    const prilozeneSouboryNew = data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                orignazev: e.name.normalize(),
                                tempnazev: e.response ? e.response.tempnazev : e.fileName
                            };
                        });

                    setPrilozeneSoubory(prilozeneSouboryNew);
                }
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...(prilozeneSoubory || [])].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    }; */

    return (
        <>
            <LeiLoginlessMenu />
            {/* language === 'en' && (
                <>
                    <Paragraph
                        text={'LEI_AKTUALIZACE_OPRAVNENI'}
                        style={{
                            display: 'block',
                            position: 'relative',
                            top: -6
                        }}
                    />
                    <p
                        style={{
                            display: 'block',
                            marginTop: 0,
                            marginBottom: 20
                        }}
                    >
                        <AnchorComponent
                            id="downloadPlnaMocBtn"
                            href={
                                window.location.href.includes('test')
                                    ? 'https://test.cdcp.cz/wp-content/uploads/md/LEI-plnaMocEN.pdf'
                                    : 'https://www.cdcp.cz/wp-content/uploads/md/LEI-plnaMocEN.pdf'
                            }
                            download="Lei power of attorney.pdf"
                            target="_blank"
                        >
                            <TranslateText>PLNA_MOC_VZOR</TranslateText>
                        </AnchorComponent>
                    </p>
                </>
            )*/}
            <LoadingWrapper loading={loading}>
                <Row
                    style={{
                        backgroundColor: '#f9f9f9',
                        padding: 40,
                        minHeight: '58vh'
                    }}
                >
                    <div
                        style={{
                            width: 1200,
                            margin: '0 auto'
                        }}
                    >
                        <Col
                            span={16}
                            style={{
                                marginTop: 30,
                                display: 'block',
                                padding: '0px 20px',
                                backgroundColor: '#f9f9f9',
                                minWidth: '100%'
                            }}
                        >
                            <FormWrapper outForm={form} onFinish={onFinish} /* preserve={false}*/>
                                <SelectComponent
                                    isFormItem={true}
                                    name="typosoby"
                                    defaultValue={lei?.loginless?.osoba?.typosoby}
                                    labelParagraph="JAKA_OSOBA"
                                    data={leiTypOsobySelect}
                                    onChange={() => setForceRerender(pv => !pv)}
                                    placeholder={language === 'en' ? 'LEI_TYP_OSOBY_PLACEHOLDER' : undefined}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />
                                {/* form.getFieldValue('typosoby') === ETypOsoby.OPRAVNENA && language === 'cz' && (
                                <>
                                    <Title text={'LEI_TYP_OSOBY_OPRAVNENA'} level={5} dividerTitle={true} />
                                    {(lei?.loginless?.spolecnost?.jurisdikce === 'SK' ||
                                        lei?.loginless?.spolecnost?.jurisdikce === 'CZ') && (
                                        <Paragraph
                                            text={'LEI_AKTUALIZACE_OPRAVNENI'}
                                            style={{
                                                display: 'block',
                                                position: 'relative',
                                                top: -6
                                            }}
                                        />
                                    )}
                                    <Paragraph
                                        text={
                                            lei?.loginless?.spolecnost?.jurisdikce === 'SK' ||
                                            lei?.loginless?.spolecnost?.jurisdikce === 'CZ'
                                                ? 'LEI_AKTUALIZACE_OPRAVNENI_SUB'
                                                : 'LEI_AKTUALIZACE_OPRAVNENI_SUB_ZAHRANICNI'
                                        }
                                        style={{
                                            display: 'block',
                                            position: 'relative',
                                            top:
                                                lei?.loginless?.spolecnost?.jurisdikce !== 'SK' &&
                                                lei?.loginless?.spolecnost?.jurisdikce !== 'CZ'
                                                    ? 2
                                                    : 6
                                        }}
                                    />
                                    <p
                                        style={{
                                            display: 'block',
                                            marginTop: 2,
                                            marginBottom: 20
                                        }}
                                    >
                                        <AnchorComponent
                                            id="downloadPlnaMocBtn"
                                            href={
                                                window.location.href.includes('test')
                                                    ? 'https://test.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                                    : 'https://www.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                            }
                                            download="Lei plná moc.pdf"
                                            target="_blank"
                                        >
                                            <TranslateText>PLNA_MOC_VZOR</TranslateText>
                                        </AnchorComponent>
                                    </p>
                                </>
                            )*/}
                                {/* form.getFieldValue('typosoby') === ETypOsoby.POVERENA && language === 'cz' && (
                                <>
                                    <Title text={'LEI_TYP_OSOBY_POVERENA'} level={5} dividerTitle={true} />
                                    <Paragraph
                                        text={'LEI_AKTUALIZACE_OPRAVNENI2'}
                                        style={{
                                            display: 'block',
                                            position: 'relative',
                                            top: -6
                                        }}
                                    />{' '}
                                    <p
                                        style={{
                                            display: 'block',
                                            position: 'relative',
                                            top: -8,
                                            marginBottom: 20
                                        }}
                                    >
                                        <AnchorComponent
                                            id="downloadPlnaMocBtn"
                                            href={
                                                window.location.href.includes('test')
                                                    ? 'https://test.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                                    : 'https://www.cdcp.cz/wp-content/uploads/md/LEI-plnaMoc.pdf'
                                            }
                                            download="Lei plná moc.pdf"
                                            target="_blank"
                                        >
                                            <TranslateText>PLNA_MOC_VZOR</TranslateText>
                                        </AnchorComponent>
                                    </p>
                                </>
                            ) */}
                                <InputComponent
                                    label="JMENO"
                                    name="jmeno"
                                    initialValue={lei?.loginless?.osoba?.jmeno}
                                    maxLength={100}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />
                                <InputComponent
                                    label="PRIJMENI"
                                    name="prijmeni"
                                    initialValue={lei?.loginless?.osoba?.prijmeni}
                                    maxLength={100}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />
                                <InputComponent
                                    label="PRACOVNI_POZICE"
                                    name="pozice"
                                    initialValue={lei?.loginless?.osoba?.pozice}
                                    maxLength={500}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />

                                {/* <Form.Item
                                name="soubor"
                                label={<Paragraph text={'SOUBOR'} strong={true} />}
                                rules={[
                                    {
                                        required: false
                                         required:
                                                lei?.loginless?.spolecnost?.jurisdikce !== 'CZ' &&
                                                lei?.loginless?.spolecnost?.jurisdikce !== 'SK',
                                            validator() {
                                                try {
                                                    if (!fileList || fileList.length < 1)
                                                        return Promise.reject(
                                                            <TranslateText>
                                                                NEVALIDNI_POVINNA_POLOZKA_UPLOAD
                                                            </TranslateText>
                                                        );
                                                    return Promise.resolve();
                                                } catch {
                                                    return Promise.reject();
                                                }
                                            }
                                    }
                                ]}
                            >
                                <div
                                    style={{
                                        marginBottom: 22
                                    }}
                                >
                                    {lei?.loginless?.spolecnost?.jurisdikce !== 'CZ' &&
                                        lei?.loginless?.spolecnost?.jurisdikce !== 'SK' && (
                                            <p>
                                                <TranslateText>LEI_VLOZTE_REJSTRIK</TranslateText>
                                            </p>
                                        )}
                                    <UploadComponent
                                        {...uploadProps}
                                        accept={'.pdf, .rtf, .docx, .doc'}
                                        id="osobaUploadBtn"
                                        disabled={!!userCheckResp || !!emailOvereniResp}
                                    />
                                </div>
                            </Form.Item> */}
                                <Title text={'KONTAKTNI_UDAJE'} level={5} dividerTitle={true} />
                                <InputComponent
                                    label="EMAIL"
                                    name="email1"
                                    infoTooltipText="EMAIL_TOOLTIP"
                                    initialValue={lei?.loginless?.osoba?.email1}
                                    maxLength={128}
                                    rules={emailValidation()}
                                    onChange={() => setForceRerender(pv => !pv)}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                    labelExtra={
                                        <Button
                                            id="c2PlusAltBtn"
                                            htmlType="button"
                                            type="primary"
                                            style={{
                                                display: 'inline-block',
                                                marginLeft: 20,
                                                width: 50
                                            }}
                                            disabled={!!userCheckResp || !!emailOvereniResp}
                                            icon={!showEmail2 ? <PlusOutlined /> : <MinusOutlined />}
                                            onClick={() => {
                                                setShowEmail2(pv => !pv);
                                            }}
                                        />
                                    }
                                />
                                {showEmail2 && (
                                    <InputComponent
                                        label="EMAIL2"
                                        name="email2"
                                        initialValue={lei?.loginless?.osoba?.email2}
                                        notRequired={true}
                                        maxLength={128}
                                        rules={[
                                            {
                                                required: false
                                            },
                                            () => ({
                                                validator(_rule: RuleObject, value: string) {
                                                    if (value && (value.match(/@/g) || []).length > 1)
                                                        return Promise.reject(
                                                            <TranslateText>NEVALIDNI_EMAIL_TWO</TranslateText>
                                                        );
                                                    if (!value || emailRegExp.test(value)) return Promise.resolve();
                                                    return Promise.reject(
                                                        <TranslateText>NEVALIDNI_EMAIL</TranslateText>
                                                    );
                                                }
                                            })
                                        ]}
                                        disabled={!!userCheckResp || !!emailOvereniResp}
                                    />
                                )}
                                <InputComponent
                                    label="EMAIL_FAKTURACANI"
                                    name="emailfakt"
                                    initialValue={lei?.loginless?.osoba?.emailfakt}
                                    maxLength={128}
                                    rules={emailValidation()}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />
                                <InputComponent
                                    label="TELEFON"
                                    name="telefon"
                                    initialValue={lei?.loginless?.osoba?.telefon}
                                    maxLength={17}
                                    rules={phoneValidation()}
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />
                                <SelectComponent
                                    name="madic"
                                    labelParagraph="ISIN_IDENTIFIKACE_PLATCE"
                                    isFormItem={true}
                                    data={anoNe}
                                    onChange={() => {
                                        form.resetFields(['dic']);
                                        setForceRerender(pv => !pv);
                                    }}
                                    defaultValue={
                                        lei?.loginless?.osoba && typeof lei?.loginless?.osoba?.madic === 'boolean'
                                            ? lei?.loginless?.osoba?.madic
                                                ? EAnoNe.ANO
                                                : EAnoNe.NE
                                            : undefined
                                    }
                                    disabled={!!userCheckResp || !!emailOvereniResp}
                                />
                                {form.getFieldValue('madic') === EAnoNe.ANO && (
                                    <InputComponent
                                        initialValue={lei?.loginless?.osoba?.dic}
                                        label="ISIN_IDENTIFIKACE_DIC"
                                        name="dic"
                                        maxLength={14}
                                        rules={dicValidation()}
                                        disabled={!!userCheckResp || !!emailOvereniResp}
                                    />
                                )}

                                {!userCheckResp && (
                                    <>
                                        {!emailOvereniResp ? (
                                            <>
                                                <LoadingWrapper loading={loadingCaptcha}>
                                                    <InputComponent
                                                        label="OPISTE_TEXT_Z_OBRAZKU"
                                                        name="captcha"
                                                        type="text"
                                                        idCaptcha={captchaState.id}
                                                        imageCaptcha={captchaState.image}
                                                        onChange={() => setErrCaptcha(undefined)}
                                                        disabled={!!emailOvereniResp}
                                                    />
                                                </LoadingWrapper>
                                                <ButtonComponent
                                                    buttontext="EMAIL_OVERIT"
                                                    onClick={() => form.submit()}
                                                    id="submitBtn"
                                                    loading={loading}
                                                    extramessage={errCaptcha}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <InputComponent label="EMAIL_OVERIT_KOD" name="emailovereni" />
                                                {/* <Paragraph text="EMAIL_OVERIT_KOD_TEXT" /> */}
                                            </>
                                        )}
                                    </>
                                )}
                                {userCheckResp?.stav === EStavUzivatele.MUSI ||
                                userCheckResp?.stav === EStavUzivatele.MUZE ? (
                                    <LoginCollapse loginOptions={userCheckResp.typyuctu} setLoading={setLoading} />
                                ) : (
                                    <>
                                        {(userCheckResp || emailOvereniResp) && (
                                            <Row
                                                style={{
                                                    marginTop: 20
                                                }}
                                            >
                                                <ButtonComponent
                                                    onClick={() => {
                                                        setUserCheckResp(undefined);
                                                        setEmailOvereniResp(undefined);
                                                        callCaptcha(setLoadingCaptcha, setCaptchaState, setErrCaptcha);
                                                    }}
                                                    buttontext="ZPET"
                                                    id="osobaZpetBtn"
                                                    type="default"
                                                />

                                                <ButtonComponent
                                                    id="osobaPokracovatBtn"
                                                    style={{
                                                        margin: '0 8px'
                                                    }}
                                                    type="primary"
                                                    onClick={() => form.submit()}
                                                    //  loading={uploading}
                                                    buttontext={'POKRACOVAT'}
                                                    // extramessage={fileErr ? 'SOUBOR_NOT_LOADED_OSOBA' : ''}
                                                />
                                            </Row>
                                        )}
                                    </>
                                )}
                            </FormWrapper>
                        </Col>
                    </div>
                </Row>
            </LoadingWrapper>
            <LeiLoginlessFooter />
        </>
    );
};

export default IdentifikaceOsoby;
